import ReactEcharts, { EChartsOption } from 'echarts-for-react'
import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { ReactNode, useMemo } from 'react'

import { Translation } from 'cng-common/features/translations'

import styles from './SobLineChart.module.scss'

export interface IChartSettings {
  name: string
  data: (number | null)[]
  lineType: string
  color?: string
}

interface ISobLineChartProps {
  linesSettings: IChartSettings[]
  selectedPeriodFYear: number
  chartName: string
  chartLabelNode?: ReactNode
  height?: number
}

export const SobLineChart = ({
  linesSettings,
  selectedPeriodFYear,
  chartName,
  chartLabelNode,
  height,
}: ISobLineChartProps) => {
  const options = useMemo(() => {
    const linesSeries = linesSettings.map((el) => ({
      name: el.name,
      type: 'line',
      label: {
        show: true,
        position: 'top',
        formatter: '{c}%',
      },
      itemStyle: {
        color: el.color,
      },
      lineStyle: {
        type: el.lineType,
      },
      tooltip: {
        valueFormatter: function (value: any) {
          if (value) {
            return value + '%'
          }
          return '-'
        },
      },
      data: el.data.map((value: number | null) => {
        if (Number.isFinite(value)) {
          if (!value) {
            return null
          }
          return value.toFixed(1)
        }
        return value
      }),
    }))

    const previousFYear = selectedPeriodFYear - 1
    const previousFYearLastTwoDigits = previousFYear.toString().slice(-2)
    const prevYearQuarters = Array.from({ length: 4 }, (_, i) => {
      const quarter = i + 1
      return `Q${quarter}'${previousFYearLastTwoDigits}`
    })
    const selectedFYearLastTwoDigits = selectedPeriodFYear.toString().slice(-2)

    const selectedYearQuarters = Array.from({ length: 4 }, (_, i) => {
      const quarter = i + 1
      return `Q${quarter}'${selectedFYearLastTwoDigits}`
    })

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: '#999',
          },
        },
      },
      legend: {
        bottom: 0,
        data: linesSettings.map((el) => el.name),
      },
      xAxis: [
        {
          type: 'category',
          data: [...prevYearQuarters, ...selectedYearQuarters],
          axisPointer: {
            type: 'shadow',
          },
        },
      ],
      yAxis: {
        type: 'value',
      },
      series: linesSeries,
    } as EChartsOption
  }, [linesSettings, selectedPeriodFYear])

  return (
    <div className={styles.root}>
      <RenderWhen condition={Boolean(chartName)}>
        <div className={styles.chartTitle}>
          <Translation span text={chartName} />
        </div>
      </RenderWhen>
      <div className={styles.chartWrapper}>
        <RenderWhen condition={Boolean(chartLabelNode)}>
          <div className={styles.currencySettings}>{chartLabelNode}</div>
        </RenderWhen>

        <ReactEcharts option={options} opts={{ renderer: 'svg', height }} />
      </div>
    </div>
  )
}
